import { BigNumber } from "ethers"
import { commify } from "ethers/lib/utils"
import { t } from "i18next"
import { useCallback, useRef } from "react"
import { enqueueToast } from "src/components/Toastify"
import { FER, XFER } from "src/constants"
import { BIG_ZERO } from "src/constants/bigNumber"
import useFerroBarHandlers from "src/pages/Stake/hooks/useFerroBarHandlers"
import { useUserFerroBar } from "src/state/xFer/hooks"
import { formatBNToString } from "src/utils"

export function useStakePartFer(
  lockAmount: BigNumber,
  harvest?: () => Promise<void>,
) {
  const { onEnterPool, onEnterPoolStatic, onApprove } = useFerroBarHandlers()
  const { barData } = useUserFerroBar()
  const { allowance } = barData

  const xFerAmount = useRef(BIG_ZERO)
  const isEnabled = allowance && allowance.gt("0")
  const formattedLockAmount = formatBNToString(lockAmount, FER.decimals)
  const stake = useCallback(async () => {
    try {
      xFerAmount.current = await onEnterPoolStatic(formattedLockAmount)
      await onEnterPool(formattedLockAmount, [
        {
          label: t("Stake"),
          token: FER,
          amount: commify(formatBNToString(lockAmount, FER.decimals, 2)),
        },
        {
          label: t("Receive"),
          token: XFER,
          amount: commify(
            formatBNToString(xFerAmount.current, XFER.decimals, 2),
          ),
        },
      ])
    } catch (e) {
      enqueueToast(
        "error",
        t("Your FER amount has been reverted"),
        t("Transaction Failed"),
      )
      throw e
    }
  }, [onEnterPool, onEnterPoolStatic, lockAmount, formattedLockAmount])

  const checkAndApprove = async () => {
    try {
      if (!isEnabled) {
        await onApprove()
      }
    } catch (e) {
      enqueueToast("error", t("FER is not approved"), t("Transaction Failed"))
      throw e
    }
  }

  const claimAndStake = async () => {
    try {
      if (harvest) {
        await harvest()
        await checkAndApprove()
        await stake()
      }
    } catch (e) {
      console.error(e)
      throw e
    }
  }

  return {
    claimAndStake,
    xFerAmount,
  }
}
